import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  DndContext, closestCenter, MeasuringStrategy, MouseSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ── Icons ─────────────────────────────────────────────

const TrashIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 5.98047C17.67 5.65047 14.32 5.48047 10.98 5.48047C9 5.48047 7.02 5.58047 5.04 5.78047L3 5.98047"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18.85 9.14062L18.2 19.2106C18.09 20.7806 18 22.0006 15.21 22.0006H8.79002C6.00002 22.0006 5.91002 20.7806 5.80002 19.2106L5.15002 9.14062"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10.33 16.5H13.66" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.5 12.5H14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.9201 8.9502L13.4001 15.4702C12.6301 16.2402 11.3701 16.2402 10.6001 15.4702L4.08008 8.9502"
      stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);


const PlusIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" />
  </svg>
);

// ── Hilfsfunktionen ───────────────────────────────────────────

function makeId() {
  return `id-${Math.random().toString(36).slice(2)}`;
}

function withIds(arr) {
  return (arr || []).map(item =>
    item._id ? item : { ...item, _id: makeId() }
  );
}

// Deutsches Datum in eine sortierbare Zahl (Jahr*100 + Monat) umwandeln.
// Es zählt der Startzeitpunkt (Teil vor dem Bindestrich). Leeres Datum -> ganz oben.
const MONATE = {
  jan: 1, feb: 2, mär: 3, mrz: 3, maer: 3, apr: 4, mai: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, okt: 10, nov: 11, dez: 12,
};
function datumStartKey(datum) {
  const raw = (datum || '').trim().toLowerCase();
  if (!raw) return Infinity; // leer/neu -> oben
  const start = raw.split(/[–—-]/)[0].trim();
  let m = start.match(/(\d{1,2})[./](\d{4})/);           // 06/2024 oder 06.2024
  if (m) return parseInt(m[2], 10) * 100 + parseInt(m[1], 10);
  m = start.match(/([a-zä]+)\.?\s*(\d{4})/);              // Jan. 2026 / Mai 2023
  if (m) return parseInt(m[2], 10) * 100 + (MONATE[m[1].slice(0, 3)] || MONATE[m[1]] || 0);
  m = start.match(/(\d{4})/);                            // nur Jahr
  if (m) return parseInt(m[1], 10) * 100;
  return 0;
}

const DragIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="2" y1="7"  x2="22" y2="7"  stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="2" y1="17" x2="22" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// Berufserfahrungs-Eintrag ohne jeglichen Inhalt (Titel, Arbeitgeber, Datum, Aufgaben alle leer)
function istLeererEintrag(e) {
  return !(e.jobtitel || e.rolle || '').trim()
    && !(e.arbeitgeber || e.firma || '').trim()
    && !(e.datum || '').trim()
    && (e.bullets || []).every(b => !(b || '').trim());
}

// ── EintraegeEditor ───────────────────────────────────────────

function SortableBullet({ id, isNew, onAnimationEnd, children }) {
  // ponytail: transition=undefined nach dem Drop → kein Rücksprung-Bounce
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition: isDragging ? transition : undefined, opacity: isDragging ? 0.45 : 1 }}
      className={`ssp-cv-bullet${isNew ? ' is-new' : ''}`}
      onAnimationEnd={onAnimationEnd}
    >
      {/* Der Punkt selbst ist der Griff — hover tauscht • gegen ≡ */}
      <button className="ssp-cv-bullet-dot" {...attributes} {...listeners} tabIndex={-1} title="Verschieben">
        <span className="ssp-cv-bullet-dot-char">•</span>
        <span className="ssp-cv-bullet-dot-grip"><DragIcon size={15} /></span>
      </button>
      {children}
    </div>
  );
}

function AufgabenEditor({ eintrag, onChange, footer }) {
  const bullets = (eintrag.bullets && eintrag.bullets.length) ? eintrag.bullets : [''];
  const inputsRef = useRef([]);
  const focusIdx = useRef(null);
  const [newIdx, setNewIdx] = useState(null);
  // Stabile IDs pro Stichpunkt — verhindert React-Key-Wechsel beim Umsortieren
  const [ids, setIds] = useState(() => bullets.map(() => makeId()));
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 4 } }), useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }));
  const containerRef = useRef(null);
  // Drag auf Bullets-Container beschränken, kein unendliches Runterziehen
  const restrictToContainer = useCallback(({ transform, draggingNodeRect }) => {
    if (!draggingNodeRect || !containerRef.current) return { ...transform, x: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    return {
      ...transform,
      x: 0,
      y: Math.min(Math.max(transform.y, rect.top - draggingNodeRect.top), rect.bottom - draggingNodeRect.bottom),
    };
  }, []);

  // IDs neu generieren wenn externe Änderung die Anzahl ändert
  useEffect(() => {
    if (ids.length !== bullets.length) setIds(bullets.map(() => makeId()));
  }, [bullets.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (focusIdx.current != null) {
      inputsRef.current[focusIdx.current]?.focus();
      focusIdx.current = null;
    }
  });

  const commit = (next) => onChange({ ...eintrag, bullets: next });
  const setAt = (i, val) => { const next = [...bullets]; next[i] = val; commit(next); };
  const addAfter = (i) => {
    const next = [...bullets]; next.splice(i + 1, 0, '');
    const nextIds = [...ids]; nextIds.splice(i + 1, 0, makeId());
    setIds(nextIds);
    focusIdx.current = i + 1; setNewIdx(i + 1);
    commit(next);
  };
  const removeAt = (i) => {
    const nextIds = ids.filter((_, j) => j !== i);
    setIds(nextIds.length ? nextIds : [makeId()]);
    const next = bullets.filter((_, j) => j !== i);
    focusIdx.current = Math.max(0, i - 1);
    commit(next.length ? next : ['']);
  };
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = ids.indexOf(active.id);
    const newIdx2 = ids.indexOf(over.id);
    if (oldIdx < 0 || newIdx2 < 0) return;
    setIds(arrayMove(ids, oldIdx, newIdx2));
    commit(arrayMove(bullets, oldIdx, newIdx2));
  };
  const effectiveIds = ids.length === bullets.length ? ids : bullets.map(() => makeId());

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToContainer]} onDragEnd={onDragEnd}>
        <SortableContext items={effectiveIds} strategy={verticalListSortingStrategy}>
          <div className="ssp-cv-bullets" ref={containerRef}>
            {bullets.map((b, i) => (
              <SortableBullet key={effectiveIds[i]} id={effectiveIds[i]} isNew={i === newIdx} onAnimationEnd={() => i === newIdx && setNewIdx(null)}>
                <textarea
                  className="ssp-cv-bullet-input"
                  rows={1}
                  value={b}
                  placeholder="Aufgabe oder Erfolg …"
                  ref={el => { inputsRef.current[i] = el; }}
                  onChange={e => setAt(i, e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); addAfter(i); }
                    else if (e.key === 'Backspace' && b === '' && bullets.length > 1) {
                      e.preventDefault(); removeAt(i);
                    }
                  }}
                />
                {bullets.length > 1 && (
                  <button className="ssp-cv-bullet-del" tabIndex={-1}
                    onClick={() => removeAt(i)}>×</button>
                )}
              </SortableBullet>
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div className="ssp-cv-footer">
        <button className="ssp-cv-bullet-add" onClick={() => addAfter(bullets.length - 1)}>
          <PlusIcon size={12} /> Stichpunkt
        </button>
        {footer}
      </div>
    </>
  );
}

// Firma und Ort liegen als ein String "Firma · Ort" im Feld arbeitgeber.
const ORT_SEP = ' · ';
function splitFirmaOrt(raw) {
  const s = raw || '';
  const i = s.indexOf(ORT_SEP);
  return i >= 0 ? [s.slice(0, i), s.slice(i + ORT_SEP.length)] : [s, ''];
}
function joinFirmaOrt(firma, ort) {
  return ort.trim() ? `${firma}${ORT_SEP}${ort}` : firma;
}
// Datum liegt als ein String "Von – Bis" im Feld datum. Für die zwei Eingabefelder
// wird es am Gedankenstrich (oder " - ") getrennt und zum Speichern wieder zusammengesetzt.
function splitVonBis(raw) {
  const parts = (raw || '').split(/\s*[–—]\s*|\s+-\s+/);
  return [(parts[0] || '').trim(), parts.slice(1).join(' - ').trim()];
}
function joinVonBis(von, bis) {
  const v = (von || '').trim();
  const b = (bis || '').trim();
  if (!v && !b) return '';
  return b ? `${v} – ${b}` : v;
}

function Eintrag({ eintrag, isExpanded, onToggle, onChange, onDelete, onDatumBlur }) {
  const [confirmDel, setConfirmDel] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: eintrag._id, animateLayoutChanges: () => false });
  const title = eintrag.arbeitgeber || eintrag.firma || eintrag.jobtitel || eintrag.rolle || '';
  const jobVal = eintrag.jobtitel || eintrag.rolle || '';
  const [firmaVal, ortVal] = splitFirmaOrt(eintrag.arbeitgeber || eintrag.firma || '');
  const [vonVal, bisVal] = splitVonBis(eintrag.datum || '');
  const setOrg = (firma, ort) =>
    onChange({ ...eintrag, arbeitgeber: joinFirmaOrt(firma, ort), firma: undefined });
  const setDatum = (von, bis) => onChange({ ...eintrag, datum: joinVonBis(von, bis) });

  // Klick außerhalb des offenen Eintrags klappt ihn zu (leere Station wird dabei verworfen).
  const rootRef = useRef(null);
  useEffect(() => {
    if (!isExpanded) return;
    const onDown = (ev) => {
      if (rootRef.current && !rootRef.current.contains(ev.target)) onToggle();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isExpanded, onToggle]);

  return (
    <div
      ref={node => { setNodeRef(node); rootRef.current = node; }}
      className={`ssp-entry${isExpanded ? ' is-open' : ''}${isDragging ? ' is-dragging' : ''}`}
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      <div className="ssp-entry-header" onClick={onToggle}>
        <span className="ssp-entry-drag" {...attributes} {...listeners} title="Ziehen zum Sortieren"
          onClick={e => e.stopPropagation()}>
          <DragIcon size={15} />
        </span>
        <span className="ssp-entry-title">{title}</span>
        <span className={`ssp-chevron${isExpanded ? ' is-open' : ''}`}>
          <ChevronIcon />
        </span>
      </div>

      <div className="ssp-entry-collapse">
        <div className="ssp-entry-collapse-clip">
        <div className="ssp-entry-fields ssp-cv-entry">
          <div className="ssp-cv-grid">
            <div className="ssp-cv-field">
              <label className="ssp-cv-lbl">Von</label>
              <input className="ssp-cv-in" value={vonVal} placeholder="Jan. 2026"
                onBlur={onDatumBlur}
                onChange={e => setDatum(e.target.value, bisVal)} />
            </div>
            <div className="ssp-cv-field">
              <label className="ssp-cv-lbl">Bis</label>
              <input className="ssp-cv-in" value={bisVal} placeholder="Heute"
                onBlur={onDatumBlur}
                onChange={e => setDatum(vonVal, e.target.value)} />
            </div>
            <div className="ssp-cv-field ssp-cv-field--full">
              <label className="ssp-cv-lbl">Position</label>
              <input className="ssp-cv-in ssp-cv-in--title" value={jobVal} placeholder="Job-Titel"
                onChange={e => onChange({ ...eintrag, jobtitel: e.target.value, rolle: undefined })} />
            </div>
            <div className="ssp-cv-field">
              <label className="ssp-cv-lbl">Firma</label>
              <input className="ssp-cv-in" value={firmaVal} placeholder="Firma"
                onChange={e => setOrg(e.target.value, ortVal)} />
            </div>
            <div className="ssp-cv-field">
              <label className="ssp-cv-lbl">Ort</label>
              <input className="ssp-cv-in" value={ortVal} placeholder="Ort"
                onChange={e => setOrg(firmaVal, e.target.value)} />
            </div>
          </div>
          <AufgabenEditor eintrag={eintrag} onChange={onChange} footer={
            confirmDel ? (
              <div className="ssp-del-confirm">
                <span className="ssp-del-confirm-text">Wirklich löschen?</span>
                <button className="ssp-del-yes" onClick={onDelete}>Ja, löschen</button>
                <button className="ssp-del-no" onClick={() => setConfirmDel(false)}>Abbrechen</button>
              </div>
            ) : (
              <button className="ssp-entry-trash" title="Eintrag löschen"
                onClick={() => setConfirmDel(true)}>
                <TrashIcon size={17} />
              </button>
            )
          } />
        </div>
        </div>
      </div>
    </div>
  );
}

function EintraegeEditor({ section, onChange, expandedId, setExpandedId }) {
  const eintraege = withIds(section.eintraege);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const update = (newEintraege, extra = {}) =>
    onChange({ ...section, ...extra, eintraege: newEintraege });

  // Leere Stichpunkt-Zeilen eines Eintrags verwerfen (beim Zuklappen/Wechseln),
  // damit sich keine leeren Kapseln ansammeln und die Vorschau sauber bleibt.
  const pruneBullets = (id) => {
    const ent = eintraege.find(x => x._id === id);
    if (!ent) return;
    const cleaned = (ent.bullets || []).filter(b => (b || '').trim());
    if (cleaned.length !== (ent.bullets || []).length) {
      update(eintraege.map(x => x._id === id ? { ...ent, bullets: cleaned } : x));
    }
  };
  // Beim Verlassen eines Eintrags: komplett leere Station verwerfen (nichts eingetragen →
  // nichts zu speichern), sonst nur die leeren Stichpunkt-Zeilen entfernen.
  const leaveEntry = (id) => {
    const ent = eintraege.find(x => x._id === id);
    if (ent && istLeererEintrag(ent)) update(eintraege.filter(x => x._id !== id));
    else pruneBullets(id);
  };
  const toggleEntry = (id) => {
    if (expandedId === id) { leaveEntry(id); setExpandedId(null); }
    else { if (expandedId) leaveEntry(expandedId); setExpandedId(id); }
  };

  // Nach Datum sortieren (neueste oben) — nur solange die Reihenfolge nicht von Hand gesetzt wurde.
  const sortByDatum = () => {
    if (section.manuellSortiert) return;
    const sorted = [...eintraege].sort((a, b) => datumStartKey(b.datum) - datumStartKey(a.datum));
    const changed = sorted.some((e, i) => e._id !== eintraege[i]._id);
    if (changed) update(sorted);
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = eintraege.findIndex(e => e._id === active.id);
    const newIdx = eintraege.findIndex(e => e._id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    // Ab jetzt gilt die Handreihenfolge und übersteuert die Datums-Automatik.
    update(arrayMove(eintraege, oldIdx, newIdx), { manuellSortiert: true });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={() => setExpandedId(null)}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={eintraege.map(e => e._id)} strategy={verticalListSortingStrategy}>
        {eintraege.map(e => (
          <Eintrag
            key={e._id}
            eintrag={e}
            isExpanded={expandedId === e._id}
            onToggle={() => toggleEntry(e._id)}
            onDatumBlur={sortByDatum}
            onChange={updated => update(eintraege.map(x => x._id === updated._id ? updated : x))}
            onDelete={() => {
              update(eintraege.filter(x => x._id !== e._id));
              if (expandedId === e._id) setExpandedId(null);
            }}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}

// ── KenntnisseEditor ──────────────────────────────────────────

function KenntnisseEditor({ section, onChange }) {
  const allKenntnisse = withIds(section.kenntnisse || []);
  const kenntnisse = allKenntnisse.filter(k => !/sprach/i.test(k.kategorie || ''));
  const update = (next) => onChange({ ...section, kenntnisse: [...allKenntnisse.filter(k => /sprach/i.test(k.kategorie || '')), ...next] });

  return (
    <div className="ssp-kenntnis-flat">
      {kenntnisse.map(skill => (
        <div key={skill._id} className="ssp-kenntnis-row">
          <input
            className="ssp-input ssp-kenntnis-input"
            value={skill.titel || ''}
            placeholder="Skill..."
            onChange={e => update(kenntnisse.map(s => s._id === skill._id ? { ...s, titel: e.target.value } : s))}
          />
          <button className="ssp-del-entry" onClick={() => update(kenntnisse.filter(s => s._id !== skill._id))} title="Entfernen">
            <TrashIcon size={13} />
          </button>
        </div>
      ))}
      <div className="ssp-kenntnis-add-row">
        <button className="ssp-add-entry" onClick={() => update([...kenntnisse, { _id: makeId(), kategorie: '', titel: '', beschreibung: '' }])}>
          <PlusIcon size={13} /> Skill hinzufügen
        </button>
      </div>
    </div>
  );
}

// ── BulletsEditor ─────────────────────────────────────────────

function BulletsEditor({ section, onChange }) {
  const bullets = section.bullets || [];
  return (
    <div className="ssp-bullets-simple">
      {bullets.map((b, i) => (
        <div key={i} className="ssp-bullet-row">
          <span className="ssp-bullet-dot">•</span>
          <input className="ssp-input ssp-bullet-input" value={b}
            onChange={e => {
              const next = [...bullets];
              next[i] = e.target.value;
              onChange({ ...section, bullets: next });
            }} />
          <button className="ssp-del-bullet" onClick={() =>
            onChange({ ...section, bullets: bullets.filter((_, j) => j !== i) })
          } title="Entfernen">
            <TrashIcon size={14} />
          </button>
        </div>
      ))}
      <button className="ssp-add-bullet" onClick={() =>
        onChange({ ...section, bullets: [...bullets, ''] })
      }>+ Punkt hinzufügen</button>
    </div>
  );
}

// ── SektionSidePanel ──────────────────────────────────────────

const SektionSidePanel = forwardRef(function SektionSidePanel({ sektion, onSave, onLiveChange, onClose, onClosingStart, inline = false }, ref) {
  const [section, setSection] = useState(() => {
    const s = sektion.section;
    if (s.typ === 'eintraege') return { ...s, eintraege: withIds(s.eintraege) };
    if (s.typ === 'kenntnisse') return { ...s, kenntnisse: withIds(s.kenntnisse) };
    return s;
  });
  const [name, setName] = useState(sektion.section.name || '');
  const [closing, setClosing] = useState(false);
  // Welcher Eintrag ist aufgeklappt (neu hinzugefügter öffnet sich sofort)
  const [expandedEntryId, setExpandedEntryId] = useState(null);
  // Wurde etwas verändert? Nur dann beim Schließen speichern.
  const dirty = useRef(false);

  // Bereinigte Sektion (ohne interne _id) für Vorschau und Speichern.
  // forSave=true entfernt zusätzlich komplett leere Einträge und leere Stichpunkte,
  // damit beim Verlassen keine leeren Punkte im Lebenslauf zurückbleiben. Während des
  // Tippens (Live-Vorschau) bleiben sie erhalten, sonst verschwänden die Skelett-Kästchen.
  const buildCleaned = (forSave = false) => {
    const clean = (arr) => (arr || []).map(({ _id, ...rest }) => rest);
    let cleaned = { ...section, name };
    if (section.typ === 'eintraege') {
      let arr = section.eintraege || [];
      if (forSave) {
        arr = arr.filter(e => !istLeererEintrag(e))
                 .map(e => ({ ...e, bullets: (e.bullets || []).filter(b => (b || '').trim()) }));
      }
      cleaned = { ...cleaned, eintraege: clean(arr) };
    }
    if (section.typ === 'kenntnisse') cleaned = { ...cleaned, kenntnisse: clean(section.kenntnisse) };
    return cleaned;
  };

  // Speichern passiert automatisch beim Schließen/Verlassen — kein Speichern-Knopf mehr.
  const persist = () => onSave(buildCleaned(true));

  // Live-Update: jede Änderung sofort in die Lebenslauf-Vorschau spiegeln (nur Anzeige,
  // kein Server-Speichern — das passiert einmalig beim Schließen).
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    onLiveChange?.(buildCleaned());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, name]);

  const handleClose = () => {
    onClosingStart?.();
    if (dirty.current) persist();
    // Eingebettet in die Sidebar: kein Ausblenden, direkt schließen
    if (inline) { onClose(); return; }
    setClosing(true);
  };

  useImperativeHandle(ref, () => ({ requestClose: handleClose }));

  const changeSection = (next) => {
    dirty.current = true;
    const isEntryDeletion =
      section.typ === 'eintraege' &&
      (next.eintraege?.length ?? 0) < (section.eintraege?.length ?? 0);
    setSection(next);
    if (isEntryDeletion) {
      dirty.current = false; // verhindert Doppel-Save beim Panel-Schließen
      const clean = (arr) => (arr || []).map(({ _id, ...rest }) => rest);
      const arr = (next.eintraege || [])
        .filter(e => !istLeererEintrag(e))
        .map(e => ({ ...e, bullets: (e.bullets || []).filter(b => (b || '').trim()) }));
      onSave?.({ ...next, name, eintraege: clean(arr) });
    }
  };
  const changeName = (v) => { dirty.current = true; setName(v); };

  const addEntry = () => {
    if (section.typ !== 'eintraege') return;
    // Schon ein komplett leerer Eintrag da? Dann keinen weiteren anlegen, nur diesen öffnen.
    const leer = (section.eintraege || []).find(istLeererEintrag);
    if (leer) { setExpandedEntryId(leer._id); return; }
    dirty.current = true;
    const id = makeId();
    setSection(s => ({ ...s, eintraege: [...(s.eintraege || []), { _id: id, datum: '', jobtitel: '', arbeitgeber: '', bullets: [] }] }));
    setExpandedEntryId(id);
  };

  return (
    <div
      className={`ssp-panel${inline ? ' ssp-panel--inline' : ''}${closing ? ' is-closing' : ''}`}
      onAnimationEnd={() => { if (closing && !inline) onClose(); }}
    >
      <div className="ssp-header">
        <div className="ssp-header-inner">
          <input
            className="ssp-name-input"
            value={name}
            onChange={e => changeName(e.target.value)}
            placeholder="Abschnittsname..."
          />
        </div>
        <button className="ssp-close" onClick={handleClose} title="Schließen">
          <CloseIcon size={16} />
        </button>
      </div>

      <div className="ssp-body">
        {section.typ === 'eintraege' && (
          <EintraegeEditor
            section={section}
            onChange={changeSection}
            expandedId={expandedEntryId}
            setExpandedId={setExpandedEntryId}
          />
        )}
        {section.typ === 'kenntnisse' && (
          <KenntnisseEditor section={section} onChange={changeSection} />
        )}
        {section.typ === 'bullets' && (
          <BulletsEditor section={section} onChange={changeSection} />
        )}
        {section.typ === 'profil' && (
          <textarea
            className="ssp-cv-profil-input"
            value={section.profil_text || ''}
            onChange={e => changeSection({ ...section, profil_text: e.target.value })}
            placeholder="Kurze Zusammenfassung..."
          />
        )}

        {section.typ === 'eintraege' && (
          <button className="ssp-add-entry" onClick={addEntry}>
            <PlusIcon size={13} /> Station hinzufügen
          </button>
        )}
      </div>
    </div>
  );
});

export default SektionSidePanel;
